import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';

interface NavbarProps {
  onLoginClick?: () => void;
}

export default function Navbar({ onLoginClick }: NavbarProps) {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isHashToolsOpen, setIsHashToolsOpen] = useState(false);
  const [isLangOpen, setIsLangOpen] = useState(false);
  const hashToolsRef = useRef<HTMLDivElement>(null);
  const langRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();

  const languages = [
    { code: 'zh', label: '中文', flag: '🇨🇳' },
    { code: 'en', label: 'English', flag: '🇺🇸' },
  ];

  const currentLang = languages.find((l) => l.code === i18n.language) || languages[0];

  const handleLangChange = (code: string) => {
    i18n.changeLanguage(code);
    setIsLangOpen(false);
  };

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (hashToolsRef.current && !hashToolsRef.current.contains(event.target as Node)) {
        setIsHashToolsOpen(false);
      }
      if (langRef.current && !langRef.current.contains(event.target as Node)) {
        setIsLangOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const checkLoginStatus = () => {
      const token = localStorage.getItem('authToken');
      const expiresAt = localStorage.getItem('authTokenExpiresAt');
      if (token && expiresAt) {
        const isExpired = Date.now() > parseInt(expiresAt);
        setIsLoggedIn(!isExpired);
      } else {
        setIsLoggedIn(false);
      }
    };
    checkLoginStatus();
    window.addEventListener('storage', checkLoginStatus);
    window.addEventListener('authStatusChanged', checkLoginStatus);
    return () => {
      window.removeEventListener('storage', checkLoginStatus);
      window.removeEventListener('authStatusChanged', checkLoginStatus);
    };
  }, []);

  const handleLoginClick = () => {
    if (onLoginClick) onLoginClick();
  };

  const handleVerifyClick = () => navigate('/verify');
  const handleUserCenterClick = () => navigate('/user-center');

  return (
    <motion.nav
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-white shadow-lg'
          : 'bg-gradient-to-r from-neutral-dark/95 to-neutral-dark/90 backdrop-blur-sm'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 py-4">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 group cursor-pointer">
            <img
              src="https://static.readdy.ai/image/66a2b9cfb9b417b2846085a20d5b8a97/39f48c36d78167ba77a6bec1ef6513fc.png"
              alt="RightChain Logo"
              className="h-10 w-10 object-contain"
            />
            <span className={`text-2xl font-bold transition-colors ${isScrolled ? 'text-neutral-dark' : 'text-white'}`}>
              RightChain
            </span>
          </Link>

          {/* Navigation Menu */}
          <div className="hidden md:flex items-center gap-8">
            <Link
              to="/"
              className={`text-base font-medium transition-all hover:scale-105 cursor-pointer whitespace-nowrap ${
                isScrolled ? 'text-neutral-dark hover:text-primary' : 'text-white hover:text-primary-lighter'
              }`}
            >
              {t('nav_home')}
            </Link>
            <a
              href="#services"
              className={`text-base font-medium transition-all hover:scale-105 cursor-pointer whitespace-nowrap ${
                isScrolled ? 'text-neutral-dark hover:text-primary' : 'text-white hover:text-primary-lighter'
              }`}
            >
              {t('nav_services')}
            </a>
            <a
              href="#how-it-works"
              className={`text-base font-medium transition-all hover:scale-105 cursor-pointer whitespace-nowrap ${
                isScrolled ? 'text-neutral-dark hover:text-primary' : 'text-white hover:text-primary-lighter'
              }`}
            >
              {t('nav_guide')}
            </a>
            <button
              onClick={handleVerifyClick}
              className={`text-base font-medium transition-all hover:scale-105 cursor-pointer whitespace-nowrap ${
                isScrolled ? 'text-neutral-dark hover:text-primary' : 'text-white hover:text-primary-lighter'
              }`}
            >
              {t('nav_verify')}
            </button>

            {/* 哈希工具下拉菜单 */}
            <div className="relative" ref={hashToolsRef}>
              <button
                onClick={() => setIsHashToolsOpen(!isHashToolsOpen)}
                className={`text-base font-medium transition-all hover:scale-105 cursor-pointer whitespace-nowrap inline-flex items-center gap-1 ${
                  isScrolled ? 'text-neutral-dark hover:text-primary' : 'text-white hover:text-primary-lighter'
                }`}
              >
                {t('nav_hash_tools')}
                <i className={`ri-arrow-down-s-line transition-transform ${isHashToolsOpen ? 'rotate-180' : ''}`}></i>
              </button>
              {isHashToolsOpen && (
                <div className="absolute top-full left-0 mt-2 w-48 bg-white rounded-lg shadow-xl border border-gray-100 py-2 z-50">
                  <a
                    href="https://hash-file.online"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors cursor-pointer"
                  >
                    <i className="ri-external-link-line text-primary"></i>
                    Hash File Online
                  </a>
                  <a
                    href="https://hash.femaster.cn"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors cursor-pointer"
                  >
                    <i className="ri-external-link-line text-primary"></i>
                    小坤哈希
                  </a>
                </div>
              )}
            </div>
          </div>

          {/* Right Side: Language Switcher + Login/User */}
          <div className="flex items-center gap-3">
            {/* 语言切换 */}
            <div className="relative" ref={langRef}>
              <button
                onClick={() => setIsLangOpen(!isLangOpen)}
                className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-all hover:scale-105 cursor-pointer whitespace-nowrap border ${
                  isScrolled
                    ? 'text-neutral-dark border-gray-200 hover:border-primary hover:text-primary bg-white'
                    : 'text-white border-white/30 hover:border-white/70 bg-white/10'
                }`}
              >
                <span className="text-base leading-none">{currentLang.flag}</span>
                <span>{currentLang.label}</span>
                <i className={`ri-arrow-down-s-line text-xs transition-transform ${isLangOpen ? 'rotate-180' : ''}`}></i>
              </button>
              {isLangOpen && (
                <div className="absolute top-full right-0 mt-2 w-36 bg-white rounded-lg shadow-xl border border-gray-100 py-1.5 z-50">
                  {languages.map((lang) => (
                    <button
                      key={lang.code}
                      onClick={() => handleLangChange(lang.code)}
                      className={`w-full flex items-center gap-2.5 px-4 py-2.5 text-sm transition-colors cursor-pointer whitespace-nowrap ${
                        i18n.language === lang.code
                          ? 'text-primary bg-primary/5 font-medium'
                          : 'text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      <span className="text-base">{lang.flag}</span>
                      <span>{lang.label}</span>
                      {i18n.language === lang.code && (
                        <i className="ri-check-line ml-auto text-primary"></i>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* 登录/个人中心 */}
            {isLoggedIn ? (
              <button
                onClick={handleUserCenterClick}
                className="bg-gradient-primary text-white px-6 py-2.5 rounded-full font-medium hover:shadow-lg hover:scale-105 transition-all cursor-pointer whitespace-nowrap inline-flex items-center gap-2"
              >
                <i className="ri-user-line"></i>
                {t('nav_user_center')}
              </button>
            ) : (
              <button
                onClick={handleLoginClick}
                className="bg-gradient-primary text-white px-6 py-2.5 rounded-full font-medium hover:shadow-lg hover:scale-105 transition-all cursor-pointer whitespace-nowrap"
              >
                {t('nav_login')}
              </button>
            )}
          </div>
        </div>
      </div>
    </motion.nav>
  );
}
